import { apiFetch } from './client';
import type { ListQueryParams } from '@lognai/frontend-core';
import type { AccessObject } from '@lognai/plugin-sdk';

export interface AccessObjectFilter {
  category?: string;
  scope?: string;
  pluginId?: string;
  isSystem?: boolean;
  search?: string;
}

export type AccessObjectFilters = ListQueryParams<AccessObjectFilter>;

export interface AccessObjectCreateInput {
  code: string;
  name: string;
  description?: string;
  category: string;
  scope: string;
  isConditional?: boolean;
  conditions?: unknown;
  pluginId?: string;
}

export interface AccessObjectUpdateInput {
  code?: string;
  name?: string;
  description?: string;
  category?: string;
  scope?: string;
  isConditional?: boolean;
  conditions?: unknown;
  isActive?: boolean;
}

const BASE_URL = '/api/admin/access-objects';

/**
 * Query keys `GET /admin/access-objects` actually reads.
 *
 * The handler reads `req.query.category` / `scope` / `pluginId` — FLAT. This
 * service built the query with `buildQueryString`, which nests everything
 * under `filter`, so it sent `?filter[category]=ORDER` and the server saw no
 * filter at all. Every caller that passed one has always received the whole
 * catalogue: the entity-partner permission picker asked for one module's
 * permissions and offered all 279 across every module (QA ledger ORD-004).
 *
 * `isActive` and `search` are declared on `AccessObjectFilter` but the
 * endpoint supports neither, so they are deliberately not sent rather than
 * sent and ignored — a parameter the server drops is indistinguishable from
 * one that works.
 */
const SUPPORTED_FILTERS = ['category', 'scope', 'pluginId'] as const;

const accessObjectQuery = (filters?: AccessObjectFilters): string => {
  const filter = filters?.filter;
  if (!filter) return '';
  const params = new URLSearchParams();
  for (const key of SUPPORTED_FILTERS) {
    const value = filter[key];
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  }
  const query = params.toString();
  return query ? `?${query}` : '';
};

export const accessObjectsService = {
  list: async (filters?: AccessObjectFilters): Promise<AccessObject[]> => {
    return apiFetch<AccessObject[]>(`${BASE_URL}${accessObjectQuery(filters)}`);
  },

  getById: async (id: string): Promise<AccessObject> => {
    return apiFetch<AccessObject>(`${BASE_URL}/${id}`);
  },

  getSystemAccessObjects: async (): Promise<AccessObject[]> => {
    return apiFetch<AccessObject[]>(`${BASE_URL}?isSystem=true`);
  },

  create: async (data: AccessObjectCreateInput): Promise<AccessObject> => {
    return apiFetch<AccessObject>(BASE_URL, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  update: async (id: string, data: AccessObjectUpdateInput): Promise<AccessObject> => {
    return apiFetch<AccessObject>(`${BASE_URL}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  delete: async (id: string): Promise<void> => {
    return apiFetch<void>(`${BASE_URL}/${id}`, { method: 'DELETE' });
  },
};
